import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import type { FHRequest } from '../../types';
import { RecurringActions } from '../RecurringActions';

interface RecurringCardProps {
  request: FHRequest;
  onUpdate: () => void;
}

function formatFrequency(info: NonNullable<FHRequest['recurring_info']>): string {
  const freq = info.frequency?.toLowerCase();
  if (freq === 'daily') return 'Every day';
  if (freq === 'weekdays' || freq === 'week days') return 'Every weekday (Mon-Fri)';
  if (freq === 'weekly' && info.weekday_name) return `Every ${info.weekday_name}`;
  if (freq === 'weekly') return 'Weekly';
  if (freq === 'monthly' && info.day_of_month) return `Monthly on the ${info.day_of_month}${getOrdinalSuffix(info.day_of_month)}`;
  if (freq === 'monthly') return 'Monthly';
  return info.frequency || 'Unknown';
}

function getOrdinalSuffix(n: number): string {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

export function RecurringCard({ request, onUpdate }: RecurringCardProps) {
  const info = request.recurring_info;
  if (!info) return null;

  const formattedNextDate = info.next_date
    ? new Date(info.next_date).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : 'Not scheduled';

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <RepeatIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">Recurring Request</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Frequency</Typography>
            <Typography variant="body2">{formatFrequency(info)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Next</Typography>
            <Typography variant="body2">{formattedNextDate}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            {!info.active ? (
              <Chip label="Stopped" size="small" color="error" variant="outlined" />
            ) : info.skip ? (
              <Chip label="Skipping Next" size="small" color="warning" variant="outlined" />
            ) : (
              <Chip label="Active" size="small" color="success" variant="outlined" />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <RecurringActions info={info} onUpdate={onUpdate} />
      </CardContent>
    </Card>
  );
}
